import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Undo2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, apiError, num, dateTime } from "@/lib/api";
import { AppShell, PageHeader, Panel, EmptyState, Btn, Tag, SkeletonRows } from "@/components/Shell";

export default function Uploads() {
  const [kinds, setKinds] = useState([]);
  const [kind, setKind] = useState("opening_stock");
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState(null);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const spec = kinds.find((k) => k.kind === kind);

  const loadBatches = async () => {
    const { data } = await api.get("/uploads/batches");
    setBatches(data);
  };

  useEffect(() => {
    api.get("/uploads/kinds").then((r) => setKinds(r.data)).catch((e) => toast.error(apiError(e)));
    loadBatches().catch((e) => toast.error(apiError(e)));
  }, []);

  const previewText = async () => {
    setBusy("preview");
    try {
      const { data } = await api.post("/uploads/preview", { kind, csv_text: text });
      setPreview({ ...data, filename: "pasted data" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const previewFile = async (file) => {
    if (!file) return;
    setBusy("preview");
    try {
      const body = new FormData();
      body.append("kind", kind);
      body.append("file", file);
      const { data } = await api.post("/uploads/preview-file", body);
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const commit = async () => {
    setBusy("commit");
    try {
      const rows = preview.rows.filter((r) => r.valid).map((r) => r.values);
      const { data } = await api.post("/uploads/commit", {
        kind, rows, filename: preview.filename || "pasted data",
      });
      toast.success(`${data.applied} row(s) applied — reversible for 30 days`);
      setPreview(null);
      setText("");
      await loadBatches();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const reverse = async (batch) => {
    try {
      await api.post(`/uploads/batches/${batch.batch_id}/reverse`);
      toast.success("Batch reversed");
      await loadBatches();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const validCount = preview?.rows.filter((r) => r.valid).length ?? 0;
  const errorCount = (preview?.rows.length ?? 0) - validCount;

  return (
    <AppShell>
      <PageHeader
        title="Excel uploads"
        subtitle="Upload any list as .xlsx, .xls or CSV — or paste it. Columns are auto-mapped, every row is validated before anything is applied, and each batch is reversible for 30 days."
        testId="uploads-header"
      />

      <div className="grid gap-4 xl:grid-cols-12">
        <Panel title="1 · Choose what you're uploading" testId="upload-kind-panel" className="xl:col-span-4">
          <div className="space-y-3 p-4">
            <div className="grid gap-px bg-slate-200">
              {kinds.map((k) => (
                <button
                  key={k.kind}
                  data-testid={`upload-kind-${k.kind}`}
                  onClick={() => { setKind(k.kind); setPreview(null); setText(""); }}
                  className={`px-3 py-2 text-left text-sm transition-colors ${
                    kind === k.kind ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {k.label}
                </button>
              ))}
            </div>
            {spec && (
              <div className="border border-slate-200 p-3">
                <p className="text-[10px] uppercase tracking-widest text-slate-500">Recognised columns</p>
                <p className="mono mt-1 text-xs text-slate-700">{spec.columns.join(", ")}</p>
                <p className="mt-2 text-[11px] text-slate-500">
                  Required: {spec.required.join(", ")}. Header names are matched loosely (qty, code,
                  description all work).
                </p>
                {spec.note && <p className="mt-2 text-[11px] text-slate-600">{spec.note}</p>}
                <Btn
                  variant="outline"
                  className="mt-3 text-xs"
                  data-testid="upload-load-sample"
                  onClick={() => setText(spec.sample)}
                >
                  Load sample rows
                </Btn>
              </div>
            )}
          </div>
        </Panel>

        <Panel title="2 · Upload or paste" testId="upload-input-panel" className="xl:col-span-8">
          <div className="space-y-3 p-4">
            <label className="flex cursor-pointer flex-col items-center gap-2 border border-dashed border-slate-300 px-4 py-8 text-center transition-colors hover:border-slate-900">
              <FileSpreadsheet className="h-6 w-6 text-slate-400" />
              <span className="text-sm text-slate-700">Choose an .xlsx, .xls or .csv file</span>
              <span className="text-xs text-slate-500">First sheet, first row = headers</span>
              <input
                ref={fileRef}
                data-testid="upload-file-input"
                type="file"
                accept=".xlsx,.xlsm,.xls,.csv,.txt"
                className="hidden"
                onChange={(e) => previewFile(e.target.files?.[0])}
              />
            </label>

            <textarea
              data-testid="upload-paste-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              placeholder={spec?.sample || "Paste CSV rows here"}
              className="mono w-full border border-slate-300 p-3 text-xs focus:border-slate-900 focus:outline-none"
            />
            <Btn data-testid="upload-preview-button" disabled={busy === "preview" || !text.trim()} onClick={previewText}>
              <Upload className="h-4 w-4" /> {busy === "preview" ? "Validating…" : "Preview & validate"}
            </Btn>
          </div>
        </Panel>

        {preview && (
          <Panel
            title={`3 · Preview — ${preview.filename}`}
            testId="upload-preview-panel"
            className="xl:col-span-12"
            right={
              <span className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-emerald-700" data-testid="preview-valid-count">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {num(validCount)} valid
                </span>
                <span className="flex items-center gap-1 text-red-600" data-testid="preview-error-count">
                  <AlertTriangle className="h-3.5 w-3.5" /> {num(errorCount)} rejected
                </span>
              </span>
            }
          >
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-600" data-testid="upload-column-mapping">
              Column mapping:{" "}
              {Object.entries(preview.mapping).map(([target, source]) => (
                <span key={target} className="mono mr-3">
                  {source} → {target}
                </span>
              ))}
              {Object.keys(preview.mapping).length === 0 && "no columns recognised — check your headers"}
            </div>
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Row</th>
                    {spec?.columns.map((c) => (
                      <th key={c} className="px-3 py-2 text-left">{c}</th>
                    ))}
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.rows.map((r) => (
                    <tr key={r.row} className={r.valid ? "" : "bg-red-50"} data-testid={`preview-row-${r.row}`}>
                      <td className="mono px-4 py-2 text-xs text-slate-500">{r.row}</td>
                      {spec?.columns.map((c) => (
                        <td key={c} className="mono px-3 py-2 text-xs">
                          {r.values[c] !== undefined && r.values[c] !== null ? String(r.values[c]) : "—"}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-xs">
                        {r.valid ? <Tag tone="green">Ready</Tag> : <span className="text-red-700">{r.errors.join("; ")}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 px-4 py-3">
              <Btn data-testid="upload-commit-button" disabled={busy === "commit" || validCount === 0} onClick={commit}>
                {busy === "commit" ? "Applying…" : `Apply ${num(validCount)} valid row(s)`}
              </Btn>
              <Btn variant="ghost" data-testid="upload-cancel-button" onClick={() => setPreview(null)}>Cancel</Btn>
              <span className="text-xs text-slate-500">Rejected rows are never applied. The batch stays reversible for 30 days.</span>
            </div>
          </Panel>
        )}

        <Panel title="Upload history — 30-day undo" testId="upload-batches-panel" className="xl:col-span-12">
          {!batches ? (
            <SkeletonRows rows={4} />
          ) : batches.length === 0 ? (
            <EmptyState icon={FileSpreadsheet} title="No uploads yet" body="Uploaded batches appear here with a one-click undo for 30 days." testId="upload-batches-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Batch</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Source</th>
                    <th className="px-3 py-2 text-right">Rows</th>
                    <th className="px-3 py-2 text-left">By</th>
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-right">Undo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {batches.map((b) => (
                    <tr key={b.id} className="hover:bg-slate-50" data-testid={`batch-row-${b.batch_id}`}>
                      <td className="mono px-4 py-2 text-xs">{b.batch_id.slice(0, 8)}</td>
                      <td className="px-3 py-2">{b.label}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{b.filename}</td>
                      <td className="num px-3 py-2 text-right">{num(b.rows_applied)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{b.user_email}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{dateTime(b.created_at)}</td>
                      <td className="px-3 py-2 text-right">
                        {b.reversed ? (
                          <Tag tone="slate">Reversed</Tag>
                        ) : b.can_reverse ? (
                          <Btn variant="danger" className="px-2 py-1 text-xs" data-testid={`batch-undo-${b.batch_id}`} onClick={() => reverse(b)}>
                            <Undo2 className="h-3 w-3" /> Undo
                          </Btn>
                        ) : (
                          <span className="text-xs text-slate-400">Window closed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </AppShell>
  );
}
